import { SocialLoginModule } from '@abacritt/angularx-social-login';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Overlay } from '@angular/cdk/overlay';
import { PRECONNECT_CHECK_BLOCKLIST } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import {
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    inject,
    Injector,
    LOCALE_ID,
    provideAppInitializer,
    provideZoneChangeDetection,
    Sanitizer,
} from '@angular/core';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';
import { MAT_CHIPS_DEFAULT_OPTIONS } from '@angular/material/chips';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_SELECT_CONFIG, MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { DomSanitizer, REMOVE_STYLES_ON_COMPONENT_DESTROY, ɵDomSanitizerImpl } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withDebugTracing, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { FUSE_CONFIG } from '@fuse/services/config.service';
import { NavigationConfig } from '@fuse/types';
import { CustomTranslateLoader, getSelectedLanguage } from '@fuse/utils/translate';
import {
    CustomMissingTranslationHandler,
    GatewayService,
    GlobalErrorHandler,
    GlobalHttpInterceptor,
    PrincipalService,
    scrollFactory,
} from '@ms/core';
import { CustomErrorStateMatcher } from '@ms/core/util/validators';
import { DYNAMIC_MATCHER_PROVIDERS } from '@ng-dynamic-forms/core';
import {
    MissingTranslationHandler,
    provideTranslateService,
    TranslateLoader,
    TranslateService,
} from '@ngx-translate/core';
import { provideHotToastConfig } from '@ngxpert/public-api';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { environment } from 'environments/environment';
import { catchError, firstValueFrom, of, retry, shareReplay } from 'rxjs';
import { routes } from './app-routing';
import { PaginatorI18n } from './paginator-i18n';

export const BROWSER_SANITIZATION_PROVIDERS: Array<any> = [
    { provide: Sanitizer, useExisting: DomSanitizer },
    { provide: DomSanitizer, useClass: ɵDomSanitizerImpl },
];

export const provideConfig = (gatewayService: GatewayService) => {
    return firstValueFrom(gatewayService.socialLoginConfig);
};

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        { provide: LOCALE_ID, useFactory: getSelectedLanguage, deps: [TranslateService] },
        provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
        provideRouter(
            routes,
            withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
            withRouterConfig({ onSameUrlNavigation: 'reload' }),
            ...(environment.development ? [withDebugTracing()] : [])
        ),
        provideAppInitializer(() => {
            if (environment.development) {
                console.log('WEB-URL', window.location.href.substring(0, window.location.href.length - 1));
            }

            const gatewayService = inject(GatewayService);
            const principal = inject(PrincipalService);

            principal.accessToken = sessionStorage.getItem('access_token');
            //principal.refreshToken = sessionStorage.getItem('refresh_token');

            return firstValueFrom(
                gatewayService.serverInfo().pipe(
                    shareReplay(1),
                    retry({
                        delay: 5000,
                        count: 3,
                    }),
                    catchError(error => {
                        if (environment.development) {
                            console.error('Server info fetch failed:', error);
                        }
                        return of(null);
                    })
                )
            ).then(result => {
                if (result) {
                    environment.serverInfo = result;
                    environment.serverInfo.userTimeZone = new Date().getTimezoneOffset();
                    gatewayService.socialLoginConfigInit();
                } else {
                    environment.serverInfo = undefined;
                }
            });
        }),
        importProvidersFrom(SocialLoginModule, MatSnackBarModule),
        {
            provide: FUSE_CONFIG,
            useValue: NavigationConfig,
        },
        {
            provide: MatPaginatorIntl,
            deps: [TranslateService],
            useFactory: (translateService: TranslateService) => new PaginatorI18n(translateService),
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: GlobalHttpInterceptor,
            multi: true,
            deps: [Injector],
        },
        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
        BROWSER_SANITIZATION_PROVIDERS,
        { provide: ErrorStateMatcher, useClass: CustomErrorStateMatcher },
        { provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] },
        { provide: MAT_CHIPS_DEFAULT_OPTIONS, useValue: { separatorKeyCodes: [ENTER, COMMA] } },
        { provide: REMOVE_STYLES_ON_COMPONENT_DESTROY, useValue: true },
        provideHotToastConfig({
            stacking: 'vertical',
        }),
        {
            provide: 'SocialAuthServiceConfig',
            useFactory: provideConfig,
            deps: [GatewayService],
        },
        ...DYNAMIC_MATCHER_PROVIDERS,
        { provide: MAT_SELECT_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] },
        {
            provide: MAT_SELECT_CONFIG,
            useValue: { disableOptionCentering: true },
        },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
        provideHttpClient(withInterceptorsFromDi(), withFetch()),
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useClass: CustomTranslateLoader,
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: CustomMissingTranslationHandler,
            },
        }),
        {
            provide: PRECONNECT_CHECK_BLOCKLIST,
            useValue: window.location.href.substring(0, window.location.href.length - 1),
        },
    ],
};
